import type { NavigationItem } from '@commons/bo-ui';
import type { Observable } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { WzbBoNavigationSidebarMenuComponent, wzbBoNavigationSidebarMenuIconsDirective } from '@commons/bo-ui';
import { NavigationMenuService } from '@commons/navigation-menu/navigation-menu.service';
import { CurrentRouteService } from '@commons/services/current-route.service';
import { isLogged, roles } from '@loyalty/utils-angular/stores';
import { Store as NgrxStore } from '@ngrx/store';
import { combineLatest, map } from 'rxjs';

@Component({
  selector: 'app-navigation-menu',
  imports: [WzbBoNavigationSidebarMenuComponent, wzbBoNavigationSidebarMenuIconsDirective, MatIconModule, AsyncPipe],
  template: `
    @if (vm$ | async; as vm) {
      <app-bo-navigation-sidebar-menu
        [reducible]="vm.menuConfig.reducible"
        [reduced]="vm.menuConfig.reduced"
        [reduceMode]="vm.menuConfig.reduceMode"
        [items]="vm.navigationItems"
        [activeRoute]="vm.currentRoute"
        (itemClicked)="onMenuItemClicked($event)"
      >
        @if (vm.isLogged) {
          <a [href]="vm.homeHref" menuLogo target="_self" aria-label="Aller à la page d'accueil">
            <mat-icon class="navigation-menu__logo" [svgIcon]="vm.logo" color="primary" />
          </a>
        }
        @for (item of vm.navigationItems; track item.label) {
          <mat-icon *appBoNavigationSidebarMenuIcon="item.icon!" [svgIcon]="item.icon!" />
        }
      </app-bo-navigation-sidebar-menu>
    }
  `,
  styleUrls: ['./navigation-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationMenuComponent {
  readonly #router = inject(Router);
  readonly #store = inject(NgrxStore);

  readonly #layoutMenuService = inject(NavigationMenuService);
  readonly #currentUserRoles$ = this.#store.select(roles);
  readonly #isLogged$ = this.#store.select(isLogged);

  readonly #navigationItems$: Observable<NavigationItem[]> = this.#currentUserRoles$.pipe(
    map((roles) => {
      if (!roles) {
        return [];
      }
      return this.#layoutMenuService.getMenu(roles);
    })
  );

  readonly #currentRoute$ = inject(CurrentRouteService).get();
  readonly vm$ = combineLatest([this.#isLogged$, this.#navigationItems$, this.#currentRoute$]).pipe(
    map(([isLogged, navigationItems, currentRoute]) => ({
      isLogged,
      navigationItems,
      currentRoute,
      homeHref: window.origin,
      logo: 'icons/obendy-white',
      menuConfig: {
        reduced: true,
        reducible: true,
        reduceMode: 'arrow' as const,
      },
    }))
  );

  onMenuItemClicked({ route }: NavigationItem): void {
    this.#router.navigate(['/', route]);
  }
}
